import * as Sentry from '@sentry/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fmt, getCallId, wlog } from '@/lib/webrtc-logger';
import { getCloseCodeMeaning } from '@/lib/websocket';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MessageHandler = (message: any) => void;

type ConnectionState =
  'idle' | 'connecting' | 'connected' | 'disconnecting' | 'error';

const HEARTBEAT_INTERVAL_MS = 25000;

interface WebSocketCallHook {
  isConnected: boolean;
  connectionState: ConnectionState;
  iceServers: RTCIceServer[];
  sendMessage: (message: Record<string, unknown>) => void;
  getReadyState: () => number | null;
  addMessageHandler: (action: string, handler: MessageHandler) => void;
  removeMessageHandler: (action: string) => void;
  cleanup: () => void;
}

/**
 * Get authentication information from Cognito.
 */
async function getCognitoToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token || null;
  } catch (error) {
    wlog('error', 'Failed to fetch Cognito token for signaling WebSocket', {
      component: 'use-websocket-call',
      error_name: error instanceof Error ? error.name : 'Unknown',
      error_message: error instanceof Error ? error.message : String(error),
    });
    Sentry.withScope((scope) => {
      scope.setTag('feature', 'video-calling');
      scope.setTag('component', 'use-websocket-call');
      const callId = getCallId();
      if (callId) scope.setTag('call_id', callId);
      Sentry.captureException(error);
    });
    return null;
  }
}

export function useWebSocketCall(
  websocketUrl: string,
  isActive: boolean, // Used as a flag so that the calling/websocket opens only when the place-call ui is open
): WebSocketCallHook {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>('idle');
  const [iceServers, setIceServers] = useState<RTCIceServer[]>([]);
  const socketRef = useRef<WebSocket | null>(null);
  const connectionOperationAborterRef = useRef<AbortController>();
  const isIntentionalCloseRef = useRef(false);
  const connectedAtRef = useRef<number | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Store message handlers to call when a message is received.
   * This is a Map where the key is the action type and the value is the handler function.
   * This is stored in a ref to avoid re-rendering on adding/removing handlers.
   */
  const messageHandlersRef = useRef<Map<string, MessageHandler>>(new Map());

  /**
   * Send a message via WebSocket.
   */
  const sendMessage = useCallback((message: Record<string, unknown>) => {
    if (!socketRef.current) {
      wlog(
        'error',
        fmt`Attempted to send "${String(message.action)}" but socket ref is null`,
        {
          component: 'use-websocket-call',
          message_action: message.action,
        },
      );
      return;
    }

    if (socketRef.current.readyState !== WebSocket.OPEN) {
      wlog(
        'warn',
        fmt`Attempted to send "${String(message.action)}" but socket is not open`,
        {
          component: 'use-websocket-call',
          ready_state: socketRef.current?.readyState,
          message_action: message.action,
        },
      );
      return;
    }

    try {
      const messageString = JSON.stringify(message);
      socketRef.current.send(messageString);
    } catch (error) {
      wlog('error', 'Failed to send signaling message', {
        component: 'use-websocket-call',
        message_action: message.action,
        error_name: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : String(error),
      });
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-websocket-call');
        const callId = getCallId();
        if (callId) scope.setTag('call_id', callId);
        scope.setExtra('message_action', message.action);
        Sentry.captureException(error);
      });
    }
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const sendHeartbeat = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      wlog('warn', 'Heartbeat skipped because socket is not open', {
        component: 'use-websocket-call',
        socket_exists: !!socket,
        ready_state: socket?.readyState,
      });
      return;
    }

    const heartbeat = { action: 'ping', ts: Date.now() };
    try {
      socket.send(JSON.stringify(heartbeat));
    } catch (error) {
      wlog('error', 'Heartbeat send failed', {
        component: 'use-websocket-call',
        error_name: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : String(error),
      });
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-websocket-call');
        const callId = getCallId();
        if (callId) scope.setTag('call_id', callId);
        Sentry.captureException(error);
      });
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    if (!isActive || heartbeatIntervalRef.current) {
      return;
    }

    heartbeatIntervalRef.current = setInterval(
      sendHeartbeat,
      HEARTBEAT_INTERVAL_MS,
    );
  }, [isActive, sendHeartbeat]);

  /**
   * Sets or removes a handler for a specific action that is received via WebSocket.
   */
  const addMessageHandler = useCallback(
    (action: string, handler: MessageHandler) => {
      messageHandlersRef.current.set(action, handler);
    },
    [],
  );
  const removeMessageHandler = useCallback((action: string) => {
    messageHandlersRef.current.delete(action);
  }, []);

  /**
   * Setup the WebSocket connection with abort controller support.
   */
  const setupWebSocket = useCallback(async (): Promise<WebSocket | null> => {
    /**
     * Abort any in-flight operations before starting a new one.
     */
    connectionOperationAborterRef.current?.abort();
    connectionOperationAborterRef.current = new AbortController();
    const { signal } = connectionOperationAborterRef.current;

    try {
      setConnectionState('connecting');

      if (signal.aborted) {
        setConnectionState('idle');
        return null;
      }

      // Check we are online
      if (!navigator.onLine) {
        setConnectionState('idle');
        return null;
      }

      if (signal.aborted) {
        setConnectionState('idle');
        return null;
      }

      // Get authentication token, i.e. we can't connect if we are not authenticated
      const token = await getCognitoToken();
      if (!token) {
        wlog(
          'warn',
          'Failed to get Cognito token, cannot establish signaling connection',
          { component: 'use-websocket-call' },
        );
        setConnectionState('idle');
        return null;
      }

      /**
       * Post async-operation, check again if the signal is aborted.
       */
      if (signal.aborted) {
        setConnectionState('idle');
        return null;
      }

      // Create a new websocket connection and store it in a ref
      const socket = new WebSocket(websocketUrl, [token]);
      socketRef.current = socket;

      if (signal.aborted) {
        socket.close();
        setConnectionState('idle');
        return null;
      }

      // Give it 10 seconds to connect or close the connection
      const connectionTimeout = setTimeout(() => {
        if (socket.readyState === WebSocket.CONNECTING) {
          wlog('error', 'Signaling WebSocket connection timed out after 10s', {
            component: 'use-websocket-call',
          });
          socket.close();
        }
      }, 10000);

      /**
       * When the WebSocket connection is opened, we clear the connection timeout
       * and set the connection state to true.
       */
      socket.onopen = () => {
        if (signal.aborted) {
          socket.close();
          setConnectionState('idle');
          return;
        }
        connectedAtRef.current = Date.now();
        isIntentionalCloseRef.current = false;
        wlog('info', 'Signaling WebSocket connected', {
          component: 'use-websocket-call',
        });
        clearTimeout(connectionTimeout);
        setIsConnected(true);
        setConnectionState('connected');
        startHeartbeat();
      };

      /**
       * Handle WebSocket errors by logging them.
       * We also clear the connection timeout to prevent it from closing the connection.
       */
      socket.onerror = (error) => {
        if (signal.aborted) {
          setConnectionState('idle');
          return;
        }
        wlog('error', 'Signaling WebSocket error', {
          component: 'use-websocket-call',
          error_type: error.type,
        });
        clearTimeout(connectionTimeout);
        setConnectionState('error');
      };

      /**
       * When we recieve a websocket message, parse it and then check
       * if we have a handler for the action.
       */
      socket.onmessage = async (event) => {
        if (signal.aborted) {
          return;
        }
        try {
          const message = JSON.parse(event.data);

          // Store ICE servers from connect response or refresh responses
          if (message.iceServers && Array.isArray(message.iceServers)) {
            setIceServers(message.iceServers);
          }

          if (message.action === 'pong') {
            return;
          }

          // Keep a low-volume debug log of inbound signaling actions so
          // the full per-call timeline can be reconstructed. `debug`
          // level so it can be filtered out of higher-severity views.
          wlog('debug', fmt`Signaling message: ${String(message.action)}`, {
            component: 'use-websocket-call',
            message_action: message.action,
          });

          const handler = messageHandlersRef.current.get(message.action);
          if (handler) {
            handler(message);
          }
        } catch (error) {
          wlog('error', 'Failed to parse signaling message', {
            component: 'use-websocket-call',
            error_name: error instanceof Error ? error.name : 'Unknown',
            error_message:
              error instanceof Error ? error.message : String(error),
          });
          Sentry.withScope((scope) => {
            scope.setTag('feature', 'video-calling');
            scope.setTag('component', 'use-websocket-call');
            const callId = getCallId();
            if (callId) scope.setTag('call_id', callId);
            Sentry.captureException(error);
          });
        }
      };

      /**
       * When the WebSocket connection is closed, we log the close code
       * and clear the connection timeout.
       */
      socket.onclose = (event) => {
        const wasIntentional = isIntentionalCloseRef.current;
        const connectionDurationMs = connectedAtRef.current
          ? Date.now() - connectedAtRef.current
          : null;
        const isUnexpectedAbnormal =
          !wasIntentional && event.code !== 1000 && event.code !== 1001;

        wlog(
          isUnexpectedAbnormal ? 'warn' : 'info',
          fmt`Signaling WebSocket closed (code ${event.code})`,
          {
            component: 'use-websocket-call',
            close_code: event.code,
            close_reason: event.reason,
            close_code_meaning: getCloseCodeMeaning(event.code),
            connection_duration_ms: connectionDurationMs,
            intentional: wasIntentional,
            close_type: isUnexpectedAbnormal ? 'unexpected' : 'normal',
          },
        );

        connectedAtRef.current = null;
        isIntentionalCloseRef.current = false;
        clearTimeout(connectionTimeout);
        stopHeartbeat();
        setIsConnected(false);
        setConnectionState('idle');
      };

      return socket;
    } catch (error) {
      if (signal.aborted) {
        setConnectionState('idle');
        return null;
      }
      wlog('error', 'Failed to initialize signaling WebSocket', {
        component: 'use-websocket-call',
        error_name: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : String(error),
        socket_ready_state: socketRef.current?.readyState,
      });
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-websocket-call');
        const callId = getCallId();
        if (callId) scope.setTag('call_id', callId);
        Sentry.captureException(error);
      });
      setConnectionState('error');
      throw error;
    }
  }, [startHeartbeat, stopHeartbeat, websocketUrl]);

  const getReadyState = useCallback(
    () => socketRef.current?.readyState ?? null,
    [],
  );

  const cleanup = useCallback(() => {
    connectionOperationAborterRef.current?.abort();
    stopHeartbeat();

    isIntentionalCloseRef.current = true;
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.close();
    }
    socketRef.current = null;
    connectedAtRef.current = null;
    setIsConnected(false);
    setConnectionState('idle');
    setIceServers([]);

    const handlersToClean = messageHandlersRef.current;
    handlersToClean.clear();
  }, [stopHeartbeat]);

  /**
   * Initialize the WebSocket connection when the hook is active or
   * perform cleanup when it is inactive.
   */
  useEffect(() => {
    if (isActive) {
      setupWebSocket();
    } else {
      cleanup();
    }

    return cleanup;
  }, [isActive, setupWebSocket, cleanup]);

  return useMemo(
    () => ({
      isConnected,
      connectionState,
      iceServers,
      sendMessage,
      getReadyState,
      addMessageHandler,
      removeMessageHandler,
      cleanup,
    }),
    [
      isConnected,
      connectionState,
      iceServers,
      sendMessage,
      getReadyState,
      addMessageHandler,
      removeMessageHandler,
      cleanup,
    ],
  );
}
